import { apiFetch, apiJson } from './client';
import { ApiProblem } from './problem';

/**
 * La communication (D67) : messagerie du personnel et des enseignants,
 * notifications aux étudiants, alertes de Kelassi. Contrat d'API : spécification
 * « Communication », section 5. Le même contrat sert l'application mobile : JSON
 * seul, dates ISO 8601 avec décalage, identifiants uuid, pagination par curseur
 * (`before` + `limit`, du plus récent au plus ancien, `next` dans la réponse).
 *
 * Les liens ne sont jamais des routes du front : une cible `{kind, id}` que
 * `ui/communication/targets.ts` traduit en adresse.
 */

/** Les teintes de l'application qu'un type de notification peut prendre. */
export type Tone = 'info' | 'warn' | 'violet' | 'danger' | 'muted' | 'ok';
export const TONES: readonly Tone[] = ['info', 'warn', 'violet', 'danger', 'muted', 'ok'];

export type Replies = 'none' | 'optional' | 'required';
export const REPLIES: readonly Replies[] = ['none', 'optional', 'required'];

export type Senders = 'staff' | 'staff_teachers' | 'direction';
export const SENDERS: readonly Senders[] = ['staff', 'staff_teachers', 'direction'];

export type Box = 'received' | 'sent' | 'archived';
export const BOXES: readonly Box[] = ['received', 'sent', 'archived'];

export type ReadFilter = 'all' | 'unread' | 'read';
export const READ_FILTERS: readonly ReadFilter[] = ['all', 'unread', 'read'];

/** Hors spécification : les valeurs de `period` que le front propose (vide : toute période). */
export type Period = '' | 'week' | 'month' | 'year';
export const PERIODS: readonly Period[] = ['', 'week', 'month', 'year'];

export type ConversationFilter = 'all' | 'unread' | 'groups';
export const CONVERSATION_FILTERS: readonly ConversationFilter[] = ['all', 'unread', 'groups'];

export type RecipientFilter = 'all' | 'unread' | 'replied';
export const RECIPIENT_FILTERS: readonly RecipientFilter[] = ['all', 'unread', 'replied'];

export type AttachmentOwner = 'message' | 'notification' | 'reply';

/** Les permissions de la communication : en détenir une montre la cloche. */
export const COMMUNICATION_PERMISSIONS: readonly string[] = [
    'communication.chat',
    'communication.notify',
    'communication.notify_own',
    'communication.notify_urgent',
    'communication.manage',
    'communication.receive',
];

/** Une pièce jointe : PDF, PNG ou JPEG, 5 Mo au plus, 3 par message. */
export const ATTACHMENT_TYPES: readonly string[] = ['application/pdf', 'image/png', 'image/jpeg'];
export const ATTACHMENTS_MAX = 3;

export interface Person {
    readonly id: string;
    readonly name: string;
    readonly initials: string;
}

export interface TypeRef {
    /** `kelassi` pour les alertes du système, null pour un type de l'établissement. */
    readonly code: string | null;
    readonly label: string;
    readonly tone: Tone;
    /** Hors spécification, attendu sur le détail d'une notification (brouillon : `typeId`). */
    readonly id?: string;
}

/** Ce vers quoi mène un élément : traduit en route par `targets.ts`. */
export interface Target {
    readonly kind: string;
    readonly id?: string | null;
}

export interface Attachment {
    readonly id: string;
    readonly name: string;
    readonly size: number;
    readonly mime: string;
}

export type UnreadKind = 'notification' | 'reply' | 'chat' | 'alert';

export interface UnreadItem {
    readonly kind: UnreadKind;
    readonly id: string;
    readonly title: string;
    readonly context: string | null;
    readonly excerpt: string | null;
    readonly from: Person | null;
    readonly type: TypeRef | null;
    readonly at: string;
    readonly target: Target;
}

/** La cloche : les non lues seulement, 20 au plus. */
export interface Unread {
    readonly count: number;
    readonly items: readonly UnreadItem[];
}

export interface RealtimeConfig {
    readonly hubUrl: string | null;
    readonly token: string | null;
    readonly topics: readonly string[];
    readonly expiresAt: string | null;
}

export type InboxKind = 'notification' | 'reply' | 'alert';

export interface InboxItem {
    readonly kind: InboxKind;
    readonly id: string;
    readonly title: string;
    readonly excerpt: string | null;
    readonly from: Person | null;
    readonly type: TypeRef | null;
    readonly at: string;
    readonly read: boolean;
    readonly target: Target;
    /** Hors spécification : un brouillon (boîte « Envoyées ») s'ouvre en modification. */
    readonly state?: 'draft' | 'sent';
}

export interface Inbox {
    readonly items: readonly InboxItem[];
    readonly next: string | null;
}

export interface ThreadMessage {
    readonly id: string;
    readonly body: string;
    readonly fromStudent: boolean;
    readonly author: Person | null;
    readonly at: string;
    readonly attachments?: readonly Attachment[];
}

export type TargetKind = 'student' | 'class' | 'training';

/** Un destinataire choisi à l'envoi : un élève, une classe (ou un groupe), une formation. */
export interface TargetChoice {
    readonly kind: TargetKind;
    readonly id: string;
    readonly label: string;
    readonly count: number;
}

export interface NotificationStats {
    readonly recipients: number;
    readonly read: number;
    readonly emailed: number;
    readonly emailFailed: number;
    readonly replies: number;
    readonly toHandle: number;
}

export interface RecipientRow {
    readonly recipientId: string;
    readonly studentId: string | null;
    readonly name: string;
    readonly className: string | null;
    readonly readAt: string | null;
    readonly emailedAt: string | null;
    readonly emailFailed: boolean;
    readonly lastReplyAt: string | null;
    readonly handled: boolean;
}

export interface NotificationDetail {
    readonly id: string;
    readonly type: TypeRef;
    readonly subject: string;
    readonly body: string;
    readonly from: Person | null;
    readonly sentAt: string | null;
    readonly attachments: readonly Attachment[];
    readonly repliesAllowed: boolean;
    /** Côté destinataire : son fil de réponses avec l'expéditeur. */
    readonly thread: readonly ThreadMessage[];
    readonly read: boolean;
    /** Côté expéditeur ou gestionnaire seulement. */
    readonly stats?: NotificationStats | null;
    readonly targets?: readonly TargetChoice[];
    readonly recipients?: readonly RecipientRow[] | { readonly items: readonly RecipientRow[]; readonly next: string | null } | null;
    /** Hors spécification : le curseur des destinataires quand `recipients` est un tableau. */
    readonly recipientsNext?: string | null;
    /** Hors spécification, attendus pour un brouillon (`/notifications/:id/edit`). */
    readonly state?: 'draft' | 'sent';
    readonly sendEmail?: boolean;
}

export interface ReplyThread {
    readonly recipientId: string;
    readonly name: string;
    readonly handled: boolean;
    readonly thread: readonly ThreadMessage[];
}

export interface NotificationType {
    readonly id: string;
    readonly code: string | null;
    readonly label: string;
    readonly tone: Tone;
    readonly replies: Replies;
    readonly emailDefault: boolean;
    readonly senders: Senders;
    readonly system: boolean;
    readonly position: number;
    readonly active: boolean;
}

/** Un type que le compte connecté peut envoyer (écran 140). */
export interface ComposeType {
    readonly id: string;
    readonly code: string | null;
    readonly label: string;
    readonly tone: Tone;
    readonly replies: Replies;
    readonly emailDefault: boolean;
}

export interface ComposeClass {
    readonly id: string;
    readonly name: string;
    readonly training: { readonly id: string; readonly code: string; readonly label: string };
    readonly count: number;
}

export interface ComposeTraining {
    readonly id: string;
    readonly code: string;
    readonly label: string;
    readonly count: number;
}

export interface Compose {
    readonly types: readonly ComposeType[];
    readonly targets: {
        /** Vrai : tout étudiant de l'établissement (communication.notify). */
        readonly canAny: boolean;
        readonly classes: readonly ComposeClass[];
        readonly trainings: readonly ComposeTraining[];
    };
    readonly studentSearch: boolean;
}

export interface ComposeStudent {
    readonly id: string;
    readonly name: string;
    readonly number: string | null;
    readonly className: string | null;
    readonly hasEmail: boolean;
}

export interface ComposeCount {
    readonly students: number;
    readonly withEmail: number;
}

export interface ConversationSummary {
    readonly id: string;
    readonly kind: 'direct' | 'group';
    readonly title: string | null;
    readonly members: readonly Person[];
    readonly lastMessage: { readonly excerpt: string; readonly author: { readonly id: string | null; readonly name: string; readonly initials?: string } | string | null; readonly at: string } | null;
    readonly unread: number;
}

export interface ChatMessage {
    readonly id: string;
    readonly body: string;
    readonly author: Person | null;
    readonly mine: boolean;
    readonly at: string;
    readonly attachments: readonly Attachment[];
    readonly readBy: number;
}

export interface MessagePage {
    readonly conversation: {
        readonly id: string;
        readonly kind: 'direct' | 'group';
        readonly title: string | null;
        readonly members: readonly (Person & { readonly online?: boolean })[];
    };
    readonly items: readonly ChatMessage[];
    readonly next: string | null;
}

export interface ChatPerson extends Person {
    readonly function: string | null;
}

export interface CommunicationSettings {
    readonly staffChat: boolean;
    readonly teacherChat: boolean;
    readonly teacherNotify: boolean;
    readonly chatEmailDigest: boolean;
    readonly retentionYears: number;
    readonly attachmentsMaxMb: number;
}

export interface SettingsBundle {
    readonly settings: CommunicationSettings;
    readonly types: readonly NotificationType[];
}

export interface CommunicationResult {
    readonly id: string | null;
    readonly count: number | null;
    readonly data: Readonly<Record<string, unknown>>;
}

function query(values: Readonly<Record<string, string | number | null | undefined>>): string {
    const params = new URLSearchParams();

    for (const [key, value] of Object.entries(values)) {
        if (value !== null && value !== undefined && value !== '') {
            params.set(key, String(value));
        }
    }

    const text = params.toString();

    return text === '' ? '' : `?${text}`;
}

/** Une liste rendue nue ou paginée : le front accepte les deux formes. */
function listOf<T>(value: readonly T[] | { readonly items: readonly T[] } | null | undefined): readonly T[] {
    if (value === null || value === undefined) return [];

    return Array.isArray(value) ? (value as readonly T[]) : (value as { readonly items: readonly T[] }).items;
}

export function getUnread(): Promise<Unread> {
    return apiJson<Unread>('/api/communication/unread');
}

export function getRealtime(): Promise<RealtimeConfig> {
    return apiJson<RealtimeConfig>('/api/communication/realtime');
}

export function getInbox(params: {
    readonly box: Box;
    readonly state: ReadFilter;
    readonly type?: string | null;
    readonly q?: string | null;
    readonly period?: string | null;
    readonly before?: string | null;
    readonly limit?: number;
}): Promise<Inbox> {
    return apiJson<Inbox>(`/api/communication/inbox${query({ ...params, state: params.state === 'all' ? null : params.state })}`);
}

export function getNotification(id: string, recipients: RecipientFilter = 'all', before: string | null = null): Promise<NotificationDetail> {
    return apiJson<NotificationDetail>(`/api/communication/notifications/${encodeURIComponent(id)}${query({ recipients: recipients === 'all' ? null : recipients, before })}`);
}

/** Les destinataires d'une notification, quelle que soit la forme rendue. */
export function recipientsOf(detail: NotificationDetail): { readonly items: readonly RecipientRow[]; readonly next: string | null } {
    const value = detail.recipients;

    if (value === undefined || value === null) return { items: [], next: null };

    if (Array.isArray(value)) return { items: value as readonly RecipientRow[], next: detail.recipientsNext ?? null };

    const page = value as { readonly items: readonly RecipientRow[]; readonly next: string | null };

    return { items: page.items, next: page.next ?? null };
}

export async function getReplyThread(id: string, recipientId: string): Promise<ReplyThread> {
    const raw = await apiJson<ReplyThread | readonly ThreadMessage[] | { readonly items: readonly ThreadMessage[]; readonly recipientId?: string; readonly name?: string; readonly handled?: boolean }>(
        `/api/communication/notifications/${encodeURIComponent(id)}/threads/${encodeURIComponent(recipientId)}`,
    );

    if (Array.isArray(raw)) {
        return { recipientId, name: '', handled: false, thread: raw as readonly ThreadMessage[] };
    }

    const value = raw as Partial<ReplyThread> & { readonly items?: readonly ThreadMessage[] };

    return { recipientId: value.recipientId ?? recipientId, name: value.name ?? '', handled: value.handled ?? false, thread: value.thread ?? value.items ?? [] };
}

export function getCompose(): Promise<Compose> {
    return apiJson<Compose>('/api/communication/compose');
}

export async function searchComposeStudents(q: string, classId: string | null = null): Promise<readonly ComposeStudent[]> {
    return listOf(await apiJson<readonly ComposeStudent[] | { readonly items: readonly ComposeStudent[] }>(`/api/communication/compose/students${query({ q, classId })}`));
}

export function countTargets(targets: readonly TargetChoice[]): Promise<ComposeCount> {
    return apiJson<ComposeCount>(`/api/communication/compose/count${query({ targets: JSON.stringify(targets.map(({ kind, id }) => ({ kind, id }))) })}`);
}

export async function listConversations(filter: ConversationFilter, q: string, before: string | null = null): Promise<readonly ConversationSummary[]> {
    return listOf(await apiJson<readonly ConversationSummary[] | { readonly items: readonly ConversationSummary[] }>(`/api/communication/conversations${query({ filter: filter === 'all' ? null : filter, q, before })}`));
}

export function getMessages(id: string, before: string | null = null, limit = 30): Promise<MessagePage> {
    return apiJson<MessagePage>(`/api/communication/conversations/${encodeURIComponent(id)}/messages${query({ before, limit })}`);
}

export async function searchPeople(q: string): Promise<readonly ChatPerson[]> {
    return listOf(await apiJson<readonly ChatPerson[] | { readonly items: readonly ChatPerson[] }>(`/api/communication/people${query({ q })}`));
}

export async function getSettings(): Promise<SettingsBundle> {
    const raw = await apiJson<SettingsBundle | (CommunicationSettings & { readonly types: readonly NotificationType[] })>('/api/communication/settings');

    if ('settings' in raw && typeof raw.settings === 'object' && raw.settings !== null) {
        return { settings: raw.settings, types: raw.types ?? [] };
    }

    const flat = raw as CommunicationSettings & { readonly types: readonly NotificationType[] };

    return { settings: flat, types: flat.types ?? [] };
}

/** Dépose une pièce jointe (corps brut) ; elle attend d'être citée par la commande d'envoi. */
export async function uploadAttachment(owner: AttachmentOwner, file: File): Promise<Attachment> {
    const response = await apiFetch(`/api/communication/attachments${query({ owner, name: file.name })}`, {
        method: 'POST',
        headers: { 'Content-Type': file.type === '' ? 'application/octet-stream' : file.type },
        body: file,
    });

    if (!response.ok) {
        throw await ApiProblem.from(response);
    }

    return (await response.json()) as Attachment;
}

export function attachmentPath(id: string): string {
    return `/api/communication/attachments/${encodeURIComponent(id)}`;
}

/**
 * Une commande de la communication. Comme pour la paie, la cible part en `id` et
 * en `target`, et les champs nommés de la spécification dans `values`.
 */
export function runCommunicationCommand(action: string, id: string | null, values: Readonly<Record<string, unknown>> = {}): Promise<CommunicationResult> {
    return apiJson<CommunicationResult>('/api/communication/commands', { method: 'POST', body: JSON.stringify({ action, id, target: id, values }) });
}

/** La taille d'un fichier lisible : « 214 Ko », « 1,2 Mo ». */
export function fileSize(bytes: number, locale: string): string {
    const format = new Intl.NumberFormat(locale === 'ar' ? 'ar-MA-u-nu-latn' : locale, { maximumFractionDigits: 1 });

    if (bytes < 1024 * 1024) return `${format.format(Math.max(1, Math.round(bytes / 1024)))} ${locale === 'fr' ? 'Ko' : 'KB'}`;

    return `${format.format(bytes / (1024 * 1024))} ${locale === 'fr' ? 'Mo' : 'MB'}`;
}
